/**
 * UserFriendlyReport — Conclusion-first user-readable report card layout.
 *
 * Mandate (Acharya Guruji, Feb 2026):
 *   "जातक ज्योतिष सीखने नहीं आया है — वह जानना चाहता है: क्या होगा? कब? क्यों? क्या करूँ?"
 *
 * Renders the 8 new user-friendly fields from the LLM answer:
 *   1. गुरुजी का सीधा उत्तर (guruji_direct_answer)
 *   2. मुख्य निष्कर्ष (main_conclusion: verdict + yes_or_no_hi + headline_finding)
 *   3. यह निष्कर्ष क्यों? (why_this_conclusion bullets)
 *   4. कब होगा? (when_will_happen)
 *   5. सबसे मजबूत समर्थन (strongest_support ✅)
 *   6. प्रमुख बाधाएँ (main_obstacles ⚠️)
 *   7. गुरुजी की सलाह (guruji_advice)
 *   8. सफलता की संभावना (success_probability_explained with meaning)
 *
 * Technical analysis (shastriya_analysis + PremiumReportCards) is rendered
 * INSIDE a collapsed <details> section "विस्तृत ज्योतिषीय विश्लेषण"
 * so the default view is clean & user-focused.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, CalendarClock, CheckCircle2, AlertTriangle, MessageCircleHeart,
  GaugeCircle, Lightbulb, ChevronDown, Crown,
} from 'lucide-react';
import { trackTechnicalAnalysisExpanded } from '../utils/analytics';

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.5, ease: 'easeOut' },
});

const Card = ({ children, gold, glow, testid, delay = 0 }) => (
  <motion.div
    {...fadeIn(delay)}
    className={`rounded-2xl p-5 sm:p-6 ${
      gold
        ? 'bg-gradient-to-br from-amber-950/40 via-purple-950/40 to-purple-950/20 border-2 border-gold/50'
        : 'bg-white/[0.025] border border-gold/20'
    } ${glow ? 'shadow-[0_0_30px_-12px_rgba(212,175,55,0.45)]' : ''}`}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const SectionHeader = ({ icon: Icon, label, accent }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
      accent === 'gold' ? 'bg-gold/20 text-gold' :
      accent === 'emerald' ? 'bg-emerald-500/15 text-emerald-300' :
      accent === 'amber' ? 'bg-amber-500/15 text-amber-300' :
      'bg-purple-500/15 text-purple-300'
    }`}>
      <Icon className="w-4 h-4" />
    </span>
    <h3 className="font-heading text-base sm:text-lg text-text-main">{label}</h3>
  </div>
);

const verdictTone = (verdictHi = '') => {
  const v = (verdictHi || '').toLowerCase();
  if (v.includes('प्रबल') || v.includes('शुभ') || v.includes('अनुकूल')) return 'emerald';
  if (v.includes('मध्यम')) return 'gold';
  if (v.includes('चुनौतीपूर्ण') || v.includes('संघर्ष')) return 'amber';
  if (v.includes('प्रतिकूल') || v.includes('अशुभ')) return 'rose';
  return 'gold';
};

export default function UserFriendlyReport({ answer, children }) {
  if (!answer) return null;

  const {
    guruji_direct_answer,
    main_conclusion = {},
    why_this_conclusion = [],
    when_will_happen = {},
    strongest_support = [],
    main_obstacles = [],
    guruji_advice,
    success_probability_explained = {},
  } = answer;

  const tone = verdictTone(main_conclusion.verdict_hi);

  return (
    <div className="space-y-6" data-testid="user-friendly-report">

      {/* 1. गुरुजी का सीधा उत्तर */}
      {guruji_direct_answer && (
        <Card gold glow delay={0.05} testid="card-direct-answer">
          <SectionHeader icon={MessageCircleHeart} label="गुरुजी का सीधा उत्तर" accent="gold" />
          <p className="text-text-main text-[15px] sm:text-base leading-[1.95] whitespace-pre-line font-body">
            {guruji_direct_answer}
          </p>
        </Card>
      )}

      {/* 2. मुख्य निष्कर्ष */}
      {(main_conclusion.verdict_hi || main_conclusion.headline_finding) && (
        <Card gold delay={0.1} testid="card-main-conclusion">
          <SectionHeader icon={Crown} label="मुख्य निष्कर्ष" accent="gold" />
          <div className="text-center py-3 px-2 rounded-xl bg-purple-950/40 border border-gold/30">
            {main_conclusion.yes_or_no_hi && (
              <p className={`text-3xl sm:text-4xl font-heading mb-1.5 ${
                tone === 'emerald' ? 'text-emerald-300' :
                tone === 'amber'  ? 'text-amber-300' :
                tone === 'rose'   ? 'text-rose-300' :
                'text-gold-gradient'
              }`} data-testid="conclusion-yes-no">
                {main_conclusion.yes_or_no_hi}
              </p>
            )}
            {main_conclusion.verdict_hi && (
              <p className="text-sm text-text-muted mb-2">{main_conclusion.verdict_hi}</p>
            )}
            {main_conclusion.headline_finding && (
              <p className="text-text-main text-[14px] sm:text-[15px] leading-relaxed pt-2 border-t border-gold/15">
                {main_conclusion.headline_finding}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* 3. यह निष्कर्ष क्यों? */}
      {why_this_conclusion?.length > 0 && (
        <Card delay={0.15} testid="card-why">
          <SectionHeader icon={Lightbulb} label="यह निष्कर्ष क्यों निकला?" accent="gold" />
          <ul className="space-y-2.5">
            {why_this_conclusion.map((reason, i) => (
              <li key={i} className="flex items-start gap-2.5 text-text-main text-[14px] sm:text-[15px] leading-relaxed">
                <span className="text-gold/80 mt-1 shrink-0">◆</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* 4. कब होगा? */}
      {(when_will_happen.primary_window_hi || when_will_happen.why_this_time_hi) && (
        <Card gold glow delay={0.2} testid="card-when">
          <SectionHeader icon={CalendarClock} label="कब घटित हो सकता है?" accent="gold" />
          {when_will_happen.primary_window_hi && (
            <div className="mb-3 p-3 rounded-xl bg-gold/10 border border-gold/30">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold/80 mb-1">सबसे अनुकूल अवधि</p>
              <p className="text-xl sm:text-2xl font-heading text-gold-gradient" data-testid="primary-window">
                {when_will_happen.primary_window_hi}
              </p>
            </div>
          )}
          {when_will_happen.why_this_time_hi && (
            <p className="text-text-main text-[14px] sm:text-[15px] leading-[1.85] mb-3">
              {when_will_happen.why_this_time_hi}
            </p>
          )}
          {when_will_happen.alternate_windows_hi?.length > 0 && (
            <div className="pt-3 border-t border-gold/15">
              <p className="text-[11px] uppercase tracking-[0.2em] text-text-muted mb-2">अन्य अनुकूल समय खिड़कियाँ</p>
              <ul className="space-y-1.5">
                {when_will_happen.alternate_windows_hi.map((w, i) => (
                  <li key={i} className="text-text-main text-[13px] sm:text-[14px] flex items-start gap-2">
                    <span className="text-gold/70 mt-0.5">▸</span><span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* 5 + 6. Support and Obstacles (side-by-side on desktop, stack on mobile) */}
      {(strongest_support.length > 0 || main_obstacles.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {strongest_support.length > 0 && (
            <Card delay={0.25} testid="card-support">
              <SectionHeader icon={CheckCircle2} label="सबसे मजबूत समर्थन" accent="emerald" />
              <ul className="space-y-2 text-[14px] sm:text-[15px]">
                {strongest_support.map((s, i) => (
                  <li key={i} className="text-text-main leading-relaxed">{s}</li>
                ))}
              </ul>
            </Card>
          )}
          {main_obstacles.length > 0 && (
            <Card delay={0.3} testid="card-obstacles">
              <SectionHeader icon={AlertTriangle} label="प्रमुख बाधाएँ" accent="amber" />
              <ul className="space-y-2 text-[14px] sm:text-[15px]">
                {main_obstacles.map((s, i) => (
                  <li key={i} className="text-text-main leading-relaxed">{s}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {/* 7. गुरुजी की सलाह */}
      {guruji_advice && (
        <Card gold delay={0.35} testid="card-advice">
          <SectionHeader icon={MessageCircleHeart} label="गुरुजी की सलाह" accent="gold" />
          <p className="text-text-main text-[15px] sm:text-base leading-[1.95] whitespace-pre-line">
            {guruji_advice}
          </p>
        </Card>
      )}

      {/* 8. सफलता की संभावना — label-only (no raw %) per Acharya Guruji's Mar-2026 mandate */}
      {(success_probability_explained?.label_hi || success_probability_explained?.meaning_hi) && (
        <Card delay={0.4} testid="card-probability">
          <SectionHeader icon={GaugeCircle} label="सफलता की संभावना" accent="gold" />
          <div className="flex items-center gap-4 mb-3">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" stroke="rgba(212,175,55,0.18)" strokeWidth="8" fill="none" />
                <motion.circle
                  cx="50" cy="50" r="42"
                  stroke="url(#prob-grad)"
                  strokeWidth="8" fill="none" strokeLinecap="round"
                  initial={{ strokeDasharray: '0 264' }}
                  animate={{ strokeDasharray: `${((success_probability_explained.percentage || 50) / 100) * 264} 264` }}
                  transition={{ duration: 1.3, ease: 'easeOut', delay: 0.5 }}
                />
                <defs>
                  <linearGradient id="prob-grad" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FFE08A" />
                    <stop offset="100%" stopColor="#D4AF37" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg sm:text-xl font-heading text-gold-gradient text-center leading-tight px-1" data-testid="probability-label">
                  {success_probability_explained.label_hi || '—'}
                </span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] text-text-muted">अनुमान — पूर्ण सुनिश्चित नहीं</p>
            </div>
          </div>
          {success_probability_explained.meaning_hi && (
            <p className="text-text-main text-[14px] sm:text-[15px] leading-[1.85] pt-3 border-t border-gold/15">
              {success_probability_explained.meaning_hi}
            </p>
          )}
        </Card>
      )}

      {/* विस्तृत ज्योतिषीय विश्लेषण — collapsed by default */}
      {children && (
        <details className="group rounded-2xl bg-white/[0.015] border border-gold/15 overflow-hidden"
          data-testid="detailed-analysis-details"
          onToggle={(e) => {
            // Fire GA4 event the first time the user expands the technical section.
            if (e.currentTarget.open && !e.currentTarget.dataset.fired) {
              e.currentTarget.dataset.fired = '1';
              trackTechnicalAnalysisExpanded({
                session_id: answer?.session_id,
                tier: answer?.meta?.tier || 'quick',
              });
            }
          }}
        >
          <summary className="flex items-center justify-between gap-3 cursor-pointer p-5 list-none hover:bg-gold/5 transition-colors">
            <div className="flex items-center gap-2.5">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="font-heading text-base sm:text-lg text-text-main">विस्तृत ज्योतिषीय विश्लेषण</span>
              <span className="text-[10px] text-text-muted/80 ml-1">(जिज्ञासुओं के लिए)</span>
            </div>
            <ChevronDown className="w-5 h-5 text-gold transition-transform group-open:rotate-180 shrink-0" />
          </summary>
          <div className="px-4 sm:px-5 pb-5 pt-1 space-y-6">
            {children}
          </div>
        </details>
      )}
    </div>
  );
}
